import { observer } from "mobx-react"
import * as React from "react"
import { observable, toJS } from "mobx"
import classNames from "classnames"
import * as _ from "lodash"
import { formatNumber, safeNull } from "../../common/Util"
import { TableViewAdapter } from "./TableViewAdapter"
import { QueryWhereClause } from '../../api/ApiClient'
import EventBus, { EventBusContext } from "../../common/EventBus"

export type TableViewColumn = {
  title?: string
  accessor?: string
  sortKey?: string
  renderTitle?: (column: TableViewColumn) => React.ReactNode
  renderItem?: (value: any, item: any, column: TableViewColumn) => React.ReactNode
  sortable?: boolean
  headerClassName?: string
  itemClassName?: string
}

export type SortDescriptor = {
  sortKey: string
  descending?: boolean
}

type TableViewProps = {
  adapter: TableViewAdapter
  columns: TableViewColumn[]
  pagination?: {offset: number, limit: number}
  filters?: {}
  hideRowNumbers?: boolean
  hideItemCount?: boolean
  defaultSort?: SortDescriptor
  tableClassName?: string
  onSortChanged?: (sortDescriptor: SortDescriptor | undefined) => void
  where: QueryWhereClause[]
  memberPosition?: any
  refreshTest?:any
}

@observer
export default class TableView extends React.Component<TableViewProps> {
  static defaultProps = {
    where: [],
  }

  static contextType = EventBusContext
  context!: React.ContextType<typeof EventBusContext> 

  @observable private error?: string = undefined
  @observable private items: any[] = []
  @observable private sortedColumn?: SortDescriptor = this.props.defaultSort
  @observable private loading = false
  @observable private totalItems = 0
  @observable private itemNumberOffset = 0

  buildFetchParams = () => {
    return {
      sortDescriptor: this.sortedColumn,
      offset: this.props.pagination ? this.props.pagination.offset : undefined,
      limit: this.props.pagination ? this.props.pagination.limit : undefined,
      filters: this.props.filters,
      where: this.props.where,
    }
  }

  fetchAll = (onData: (items: any[]) => void, onError: (error: string) => void, onFinished: () => void) => {
    const fetchParams = this.buildFetchParams()

    const PAGE_SIZE = 1000

    const fetch = (offset: number) => {
      fetchParams.offset = offset
      fetchParams.sortDescriptor = this.sortedColumn
      fetchParams.limit = PAGE_SIZE
      this.props.adapter.fetchData(
        fetchParams,
        (items, total) => {
          onData(items)

          if (offset + PAGE_SIZE < total) {
            fetch(offset + PAGE_SIZE)
          } else {
            onFinished()
          }
        }, error => {
          onError(error)
        }, () => {
        }
      )
    }

    fetch(0)
  }

  fetchData = () => {
    this.loading = true
    this.error = undefined

    this.props.adapter.fetchData(
      this.buildFetchParams(),
      (items: any[], total: number) => {
        this.items = items
        this.totalItems = total
        this.itemNumberOffset = this.props.pagination ? this.props.pagination.offset : 0
      }, (error: string) => {
        this.error = error
        this.items = []
      }, () => {
        this.loading = false
      }
    )
  }

  private renderHeaderColumn = (column: TableViewColumn) => {
    return column.renderTitle
      ? column.renderTitle(column)
      : column.title === undefined ? '' : column.title
  }

  private setSortColumn = (column: TableViewColumn) => {
    const sortKey = this.getSortKey(column)

    if (!sortKey) {
      this.sortedColumn = undefined
    } else if (this.sortedColumn && this.sortedColumn.sortKey === sortKey) {
      this.sortedColumn.descending = !this.sortedColumn.descending
    } else {
      this.sortedColumn = { sortKey: sortKey, descending: false }
      this.sortedColumn = { sortKey: sortKey, descending: false }
    }

    if (this.props.onSortChanged) {
      this.props.onSortChanged(this.sortedColumn)
    }

    this.fetchData()
  }

  private getSortKey = (column: TableViewColumn) => {
    return column.sortKey || column.accessor
  }

  private getSortIcon = (column: TableViewColumn) => {
    const sortDirection = this.getSortDirection(column)

    return column.sortable
      ? <i className={classNames(
        {
          'fa-sort-asc': sortDirection === 'asc',
          'fa-sort-desc': sortDirection === 'desc',
          'fa-sort': sortDirection === 'none',
        },
        'fa',
        'sort-icon'
      )}/>
      : null
  }

  private getSortDirection = (column: TableViewColumn): 'asc' | 'desc' | 'none' => {
    return (this.sortedColumn && this.sortedColumn.sortKey === this.getSortKey(column))
      ? this.sortedColumn.descending
        ? 'desc'
        : 'asc'
      : 'none'
  }

  private renderHeader = () => {
    return <thead>
    <tr>
      {
        this.props.hideRowNumbers
          ? null
          : <th/>
      }
      {
        this.props.columns.map((col, idx) =>
          <th
            key={idx}
            className={classNames(
              {
                'sortable-header': col.sortable,
              },
              `sort-dir-${this.getSortDirection(col)}`
            )}
            onClick={
              col.sortable
                ? () => this.setSortColumn(col)
                : undefined
            }
          >
            <div className={classNames(col.headerClassName, 'th-container')}>
              {this.getSortIcon(col)}
              {this.renderHeaderColumn(col)}
            </div>
          </th>
        )
      }
    </tr>
    </thead>
  }

  private renderColumn = (column: TableViewColumn, item: any) => {
    let value = column.accessor ? _.get(item, column.accessor) : undefined

    if(column.accessor == 'primaryMemberPosition.position.name' && this.props.memberPosition){   
      const foundItem = this.props.memberPosition.find((item1: any ) => item1.member.id === _.get(item,'id'))
      value = column.accessor && foundItem && foundItem.position ? foundItem.position.name : undefined
      }
    return column.renderItem ? column.renderItem(value, item, column) : value
  }

  private renderBody = () => {
    return <tbody>
    {
      this.items.map((item, idx) =>
        <tr key={idx}>
          {
            this.props.hideRowNumbers
              ? null
              : <td>{1 + idx + this.itemNumberOffset}</td>
          }
          {
            this.props.columns.map((column, idx) =>
              <td key={idx} className={column.itemClassName}>
                {this.renderColumn(column, item)}
              </td>
            )
          }
        </tr>
      )
    }
    </tbody>
  }

  private renderLoader = () => {
    return this.loading
      ? <div className="table-view-spinner-backdrop"><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"/></div>
      : null
  }

  componentDidMount (): void {
    this.fetchData()
    this.addListeners(this.context.eventBus)
  }

  componentWillUnmount (): void {
    this.removeListeners(this.context.eventBus)
  }

  private addListeners = (eventBus: EventBus) => {
    eventBus.on('refresh-member-list', this.props.refreshTest)
  }

  private removeListeners = (eventBus: EventBus) => {
    eventBus.remove(this.props.refreshTest)
  }

  componentDidUpdate (prevProps: Readonly<TableViewProps>, prevState: Readonly<{}>, snapshot?: any): void {
    if (
      !_.isEqual(toJS(this.props.pagination), toJS(prevProps.pagination))
      || !_.isEqual(toJS(this.props.filters), toJS(prevProps.filters))
      || prevProps.adapter !== this.props.adapter
      || !_.isEqual(toJS(this.props.defaultSort), toJS(prevProps.defaultSort))
      || !_.isEqual(toJS(this.props.where, { recurseEverything: true }), toJS(prevProps.where, { recurseEverything: true }))
    ) {
      if (!_.isEqual(toJS(this.props.defaultSort), toJS(prevProps.defaultSort))) {
        this.sortedColumn = this.props.defaultSort
      }
      this.fetchData()
    }
  }

  render (): React.ReactNode {
    return <div className="table-view-wrapper">
      {
        (this.items.length && !this.props.hideItemCount)
          ? <div className="table-view-item-count">Showing {formatNumber(safeNull(() => this.props.pagination!.offset + 1) || 1)} to {formatNumber(safeNull(() => this.props.pagination!.offset + this.items.length) || 1)} of {formatNumber(this.totalItems)} records</div>
          : null
      }
      <table className={classNames('table', 'table-striped', 'table-view', this.props.tableClassName)}>
        {this.renderHeader()}
        {
          this.error
            ? null
            : this.renderBody()
        }
      </table>
      {this.renderLoader()}
      {
        this.error
          ? <div className="table-view-error">{this.error || 'There was an error loading the data'}</div>
          : null
      }
    </div>
  }
}
